import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import SalesInvoiceCovertInComePage from '@/pages/PurchasesContainer/CovertSaleInCome';
import IncomeInvoicesPage from '@/pages/PurchasesContainer/IncomingInvoice';
import RequestPerHandPage from '@/pages/PurchasesContainer/RequestPerHand';
import NewRequestPerOffersPage from '@/pages/PurchasesContainer/RequestPerOffer';
import PagePremission from '@/routes/Restrictions/PagePremission';
import { lazyRouteComponent, preloadComponent } from '@/utils/lazyLoad';
import Premission from '@/utils/premissions/premission.type';

const ItemEyePage = lazyRouteComponent(() => import('@/pages/ItemEyes'));
const OffersPage = lazyRouteComponent(() => import('@/pages/Offers'));
const PurchasesRequestPage = lazyRouteComponent(
  () => import('@/pages/PurchasesContainer/PurchasesRequests'),
);
const AllPurchasesRequestsPage = lazyRouteComponent(
  () => import('@/pages/PurchasesContainer/AllPurchasesRequests'),
);
const UploadOfferHandPage = lazyRouteComponent(
  () => import('@/pages/PurchasesContainer/UploadOfferHand'),
);
const UploadOfferPage = lazyRouteComponent(
  () => import('@/pages/PurchasesContainer/UploadOffer'),
);

// Preload the most frequently accessed pages for instant transitions
preloadComponent(() => import('@/pages/Offers'));

// Define routes with optimized components
const PurchasesRoutes: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="/purchases/offers" replace />,
  },

  {
    path: 'item-eye',
    element: (
      <PagePremission prem={Premission.Prem_Supplier_Enter_Prem}>
        <ItemEyePage />
      </PagePremission>
    ),
  },
  {
    path: 'offers',
    element: (
      <PagePremission prem={Premission.Prem_Request_Purchase_Offers}>
        <OffersPage />
      </PagePremission>
    ),
  },
  {
    path: 'requests',
    element: (
      <PagePremission prem={Premission.Prem_Request_Purchase_Main}>
        <PurchasesRequestPage />
      </PagePremission>
    ),
  },
  {
    path: 'all-requests',
    element: (
      <PagePremission prem={Premission.Prem_Request_Purchase_Main}>
        <AllPurchasesRequestsPage />
      </PagePremission>
    ),
  },
  {
    path: 'new_request_per_offers',
    element: (
      <PagePremission prem={Premission.Prem_Request_Purchase_New_Request}>
        <NewRequestPerOffersPage />
      </PagePremission>
    ),
  },
  {
    path: 'new_request_per_hand',
    element: (
      <PagePremission prem={Premission.Prem_Request_Purchase_New_Request}>
        <RequestPerHandPage />
      </PagePremission>
    ),
  },
  {
    path: 'upload-offer-hand',
    element: (
      <PagePremission prem={Premission.Prem_Request_Purchase_Offers}>
        <UploadOfferHandPage />
      </PagePremission>
    ),
  },
  {
    path: 'upload-offer',
    element: (
      <PagePremission prem={Premission.Prem_Request_Purchase_Offers}>
        <UploadOfferPage />
      </PagePremission>
    ),
  },
  {
    path: 'sale-income-converter',
    element: (
      <PagePremission prem={Premission.Prem_Request_Purchase_Convert_Sale}>
        <SalesInvoiceCovertInComePage />
      </PagePremission>
    ),
  },
  {
    path: 'incoming-invoices',
    element: (
      <PagePremission prem={Premission.Prem_Request_Purchase_Open_Request}>
        <IncomeInvoicesPage />
      </PagePremission>
    ),
  },
];

// Advanced preloading API for dynamic route prefetching
export const prefetchPurchasesRoutes = {
  prefetchOffers: () => void import('@/pages/Offers'),
  prefetchRequests: () =>
    void import('@/pages/PurchasesContainer/PurchasesRequests'),
  prefetchAll: () => {
    void import('@/pages/Offers');
    void import('@/pages/PurchasesContainer/PurchasesRequests');
  },
};

export default PurchasesRoutes;
